<template>
    <div v-if="!this.$apollo.queries.purchaseOrder.loading">
        <v-toolbar flat color="orange" dark>
            <v-toolbar-title>{{purchaseOrder.xeroPoNumber}}: {{purchaseOrder.company}}</v-toolbar-title>
            <v-spacer></v-spacer>
            <PythiaLink v-model="purchaseOrder.id" type="po" />
            <v-progress-linear
                    :active="updating || $apollo.loading"
                    :indeterminate="updating || $apollo.loading"
                    absolute
                    top
                    height="5"
                    background-color="blue"
                    color="red accent-4"
            ></v-progress-linear>
        </v-toolbar>
        <v-container fluid>
            <v-toolbar flat light color="grey lighten-4">
                <v-spacer/>
                <v-btn
                        v-if="!readonly"
                        color="light-green"
                        class="mr-2"
                        dark
                        @click="saveUpdate"
                >Save</v-btn>
                <template v-if="purchaseOrder.xeroPoNumber">

                    <v-btn color="blue" dark
                           :href="xeroPurchaseOrderUrl(purchaseOrder.xeroPoId)"
                           target="_blank"
                           class="mr-2"
                    ><v-icon small left>fas fa-file-invoice</v-icon>Xero {{purchaseOrder.xeroPoNumber}}</v-btn>
                    <v-btn color="orange" dark @click="sendPoEmail"><v-icon small left>fas fa-envelope</v-icon> Email PO</v-btn>
                </template>
                <template v-else>
                    <v-btn color="blue" dark @click="createInXero"><v-icon small left>fas fa-exchange-alt</v-icon> Create In Xero</v-btn>
                    <v-chip>Old Invoice Type</v-chip>
                </template>
            </v-toolbar>

            <v-card>
                <v-toolbar color="indigo" dark flat dense>
                    <v-toolbar-title>Items</v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                    <v-row>
                        <v-col cols="12">
                            <ManagePOItems
                                    :purchase-order="purchaseOrder"
                                    :items="purchaseOrder.details"
                                    :loading="this.$apollo.queries.purchaseOrder.loading"
                                    :readonly="readonly"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <h2>Total: {{total|toCurrency}}</h2>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
            <v-row>
                <v-col cols="6">
                    <v-card>
                        <v-toolbar color="indigo" dark flat dense>
                            <v-toolbar-title>Details</v-toolbar-title>
                        </v-toolbar>

                        <v-card-text>
                            <v-row>
                                <v-col cols="12">
                                    <UserSelector
                                            v-if="!purchaseOrder.requisitioner"
                                            label="Requisitioner"
                                            :user="purchaseOrder.requisitioner"
                                            @selected="updateRequisitioner"
                                    />
                                    <v-text-field v-else
                                                  label="Requisitioner"
                                                  v-model="purchaseOrder.requisitioner" />

                                    <DatePicker v-model="purchaseOrder.date" />
                                    <VFormBase :model="purchaseOrder" :schema="schemaPurchaseOrder" :col="12"/>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="6">
                    <v-card>
                        <v-toolbar color="indigo" dark flat dense>
                            <v-toolbar-title>Contact</v-toolbar-title>
                        </v-toolbar>

                        <v-card-text>
                            <v-row>
                                <v-col cols="12">
                                    <VFormBase :model="purchaseOrder" :schema="schemaContact" :col="12"/>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
    import PURCHASE_ORDER_GET from "../graphql/PurchaseOrder/Get.gql"
    import Dashboard from "../layouts/Dashboard"
    import VFormBase from 'vuetify-form-base'
    import ManagePOItems from "../components/PurchaseOrder/ManagePOItems"
    import UserSelector from "../components/AutoCompletes/UserSelector"
    import PO_UPDATE from "../graphql/PurchaseOrder/Update.gql"
    import DatePicker from "../components/DatePicker";
    import PythiaLink from "../components/PythiaLink";
    import ApiMixin from "../mixins/ApiMixin";
    import {mapGetters} from "vuex";

    export default {
        metaInfo() {
            return {
                title: this.title
            }
        },
        mixins: [ApiMixin],
        components: {
            VFormBase, ManagePOItems, UserSelector, DatePicker, PythiaLink
        },
        created() {
            this.$emit('update:layout', Dashboard);
        },
        apollo: {
            purchaseOrder: {
                query: PURCHASE_ORDER_GET,
                variables() {
                    return {
                        id: parseInt(this.$route.params.id)
                    }
                },
                update: data => data.purchaseOrder
            },
        },
        data() {
            return {
                title: 'Purchase Order: ' + this.$route.params.id,
                tab: null,
                updating: false,
            }
        },
        watch: {
            updating(value) {
                this.$store.dispatch('notification/loading', value)
            },
            purchaseOrder(value) {
                if(value.xeroPoNumber){
                    this.title = value.xeroPoNumber
                }
            }
        },
        computed: {
            ...mapGetters('auth', [
                'hasPermission',
            ]),
            readonly() {
                return !this.hasPermission('purchase_order-update')
            },
            total() {
                let total = 0
                this.purchaseOrder.details.forEach((item) => {
                    total = total + parseInt(item.total)
                })

                return total
            },
            schemaPurchaseOrder() {
                return {
                    specialInstructions: {type: 'textarea', label: 'Special Instructions', readonly: this.readonly},
                    nonPrintedNotes: {type: 'textarea', label: 'Non-Printed Notes', readonly: this.readonly},
                }
            },

            schemaContact() {
                return {
                    contact: {type: 'text', label: 'contact', readonly: this.readonly},
                    address1: {type: 'text', label: 'address1', readonly: this.readonly},
                    address2: {type: 'text', label: 'address2', readonly: this.readonly},
                    address3: {type: 'text', label: 'address3', readonly: this.readonly},
                    town: {type: 'text', label: 'town', readonly: this.readonly},
                    county: {type: 'text', label: 'county', readonly: this.readonly},
                    postcode: {type: 'text', label: 'postcode', readonly: this.readonly},
                    telephone: {type: 'text', label: 'telephone', readonly: this.readonly},
                }
            },
        },
        methods: {
            sendPoEmail() {
                this.updating = true
                this.emailPo(this.purchaseOrder.id).then((res) => {
                    console.log(res)
                    this.refresh()
                    this.updating = false
                    this.$store.dispatch('notification/addSuccess', 'Purchase Order Emailed')
                }).catch((error) => {
                    this.refresh()
                    this.updating = false
                    this.$store.dispatch('notification/handleGraphQlError', error)
                })
            },

            createInXero() {
                this.updating = true
                this.generatePo(this.purchaseOrder.id).then((res) => {
                    console.log(res)
                    this.refresh()
                    this.updating = false
                    this.$store.dispatch('notification/addSuccess', 'Created in Xero successfully')
                }).catch((error) => {
                    this.refresh()
                    this.updating = false
                    this.$store.dispatch('notification/handleGraphQlError', error)
                })
            },

            refresh() {
                this.$apollo.queries.purchaseOrder.refetch()
            },

            xeroPurchaseOrderUrl(invoiceId) {
                return 'https://go.xero.com/Accounts/Payable/PurchaseOrders/View/' + invoiceId;
            },

            updateRequisitioner(user){
                this.purchaseOrder.requisitioner = user.email
            },

            saveUpdate() {
                let purchaseOrder = this.purchaseOrder
                delete purchaseOrder.__typename
                delete purchaseOrder.details
                this.updating = true
                this.$apollo.mutate({
                    mutation: PO_UPDATE,
                    variables: {input: purchaseOrder},
                }).then(() => {
                    this.$emit('complete')
                    this.$store.dispatch('notification/addSuccess', 'Save successful')
                    this.updating = false
                }).catch((error) => {
                    this.$store.dispatch('notification/handleGraphQlError', error)
                })
            }
        }
    }
</script>

<style scoped>

</style>